import { useEffect, useRef, useState } from 'react';
import { PageHeader } from '../components/PageHeader.js';
import { useSearchParams } from 'react-router-dom';
import { Trash } from 'lucide-react';
import { useCourses } from '../stores/courses.js';
import { useUi } from '../stores/ui.js';
import { useShop } from '../stores/shop.js';
import { STORE_LABELS, isStore } from '../lib/store.js';
import { AddCourseButton } from '../components/AddCourseButton.js';
import { StoreSwitch } from '../components/StoreSwitch.js';
import { Button } from '../ui/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { CatalogPanel } from '../components/CatalogPanel.js';
import { ListSkeleton } from '../components/TaskList.js';
import { Tabs } from '../ui/Tabs.js';

const TABS = [
  { id: 'liste', label: 'Liste' },
  { id: 'catalogue', label: 'Catalogue' },
] as const;

export function Courses(): React.ReactElement {
  const courses = useCourses((s) => s.courses);
  const loading = useCourses((s) => s.loading);
  const error = useCourses((s) => s.error);
  const load = useCourses((s) => s.load);
  const toggle = useCourses((s) => s.toggle);
  const remove = useCourses((s) => s.remove);
  const clearAll = useCourses((s) => s.clearAll);
  const clearDone = useCourses((s) => s.clearDone);
  const openCourseAdd = useUi((s) => s.openCourseAdd);
  const store = useShop((s) => s.store);
  const setStore = useShop((s) => s.setStore);
  const [searchParams, setSearchParams] = useSearchParams();
  // L'URL est la source de vérité de l'onglet : `/catalogue` redirige vers
  // `?tab=catalogue`, et un état local se serait désynchronisé si la redirection
  // arrivait alors que Courses est déjà monté.
  const tab: 'liste' | 'catalogue' =
    searchParams.get('tab') === 'catalogue' ? 'catalogue' : 'liste';
  function setTab(next: 'liste' | 'catalogue'): void {
    const params = new URLSearchParams(searchParams);
    if (next === 'catalogue') params.set('tab', 'catalogue');
    else params.delete('tab');
    setSearchParams(params, { replace: true });
  }
  const [confirming, setConfirming] = useState(false);
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Le magasin est une dépendance de ce chargement : en changer recharge la liste.
  // `load()` lit le magasin courant lui-même, il ne le reçoit pas (ADR 0011 § 6).
  useEffect(() => {
    load();
  }, [load, store]);

  useEffect(() => {
    // `?store=` impose le magasin : un lien venu d'un widget ajoute dans le magasin qu'il
    // nomme, quel que soit le dernier onglet consulté (sinon il héritait de `nota_store`).
    const requested = searchParams.get('store');
    if (!searchParams.has('q') && !isStore(requested)) return;
    if (isStore(requested)) setStore(requested);
    if (searchParams.has('q')) openCourseAdd();
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete('store');
    setSearchParams(next, { replace: true });
  }, [searchParams, openCourseAdd, setSearchParams, setStore]);

  const doneCount = courses.filter((c) => c.done).length;

  function askClearAll(): void {
    if (confirming) {
      if (confirmTimer.current) clearTimeout(confirmTimer.current);
      setConfirming(false);
      clearAll();
      return;
    }
    setConfirming(true);
    confirmTimer.current = setTimeout(() => setConfirming(false), 3000);
  }

  return (
    <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-4 md:py-6 space-y-4">
      <PageHeader
        title="Courses"
        actions={
          <>
            <StoreSwitch />
            {tab === 'liste' && <AddCourseButton primary />}
          </>
        }
      />

      {/* Le magasin est le contexte, les onglets sont la vue dedans : sur mobile il prend
          sa propre ligne, au-dessus, et toute la largeur. */}
      <div className="md:hidden">
        <StoreSwitch />
      </div>

      {/* Le Catalogue est la donnée de référence de cette page, pas une section de nav
          (ADR 0007). Même motif d'onglets que Calories. */}
      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Vue des courses" />

      {tab === 'catalogue' ? (
        <CatalogPanel />
      ) : loading ? (
        <ListSkeleton />
      ) : error ? (
        // Sans ce cas, un chargement en échec rendait « aucune course » : une liste vide
        // au lieu d'une panne — et avec `?store=` requis, un 400 s'afficherait comme
        // « tu n'as rien à acheter ».
        <div className="alerte alerte-erreur pile pile-serree" role="alert">
          <span>
            Impossible de charger la liste {STORE_LABELS[store]} · {error}
          </span>
          <Button variant="secondary" size="small" className="self-start" onClick={() => load()}>
            Réessayer
          </Button>
        </div>
      ) : courses.length === 0 ? (
        <EmptyState>Rien à acheter à {STORE_LABELS[store]}.</EmptyState>
      ) : (
        <>
          {/* `.ligne` du kit, sur un <label> : toute la ligne coche. `data-fait` raye le titre. */}
          <div className="liste">
            {courses.map((c) => (
              <label
                key={c.courseId}
                className="ligne ligne-cliquable"
                data-fait={c.done || undefined}
              >
                <input
                  type="checkbox"
                  checked={c.done}
                  onChange={(e) => toggle(c.courseId, e.target.checked)}
                  className="coche"
                />
                <span className="ligne-corps">
                  <span className="ligne-titre">{c.title}</span>
                </span>
                {c.qty > 1 && <span className="ligne-repere">×{c.qty}</span>}
                <Button
                  variant="quiet"
                  size="icon-small"
                  onClick={() => remove(c.courseId)}
                  className="ligne-action"
                  aria-label={`Supprimer ${c.title}`}
                >
                  <Trash size={16} aria-hidden />
                </Button>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-2 pt-2">
            {doneCount > 0 && (
              <Button variant="quiet" size="small" onClick={clearDone}>
                Vider {doneCount} coché{doneCount > 1 ? 's' : ''}
              </Button>
            )}
            <Button variant={confirming ? 'danger' : 'ghost'} size="small" onClick={askClearAll}>
              {confirming
                ? `Vraiment vider ${courses.length} article${courses.length > 1 ? 's' : ''} ?`
                : 'Tout vider'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
